import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import * as S from './KeywordsAdsSection.styles';
import { KeywordsAdsSectionProps } from './KeywordsAdsSection.types';
import { AdCards } from '@/components/AdCards/AdCards';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import {
  AdTransitionSource,
  SearchTransitionSource,
} from '@/api/types/ads.types';
import { Button } from '@/components/UI/Button/Button';
import { useResponsive } from '@/hooks/useResponsive';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { useSearchStore } from '@/store/useSearchStore';
import { cleanupSeoUrl } from '@/utils/helpers';

export const KeywordsAdsSection: React.FC<KeywordsAdsSectionProps> = ({
  ads,
  viewAllResultsHref,
  title,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const router = useRouter();
  const setSearchTransitionSource = useSearchStore(
    state => state.setSearchTransitionSource
  );

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setSearchTransitionSource(SearchTransitionSource.KeywordsAds);
    if (viewAllResultsHref) {
      router.push(viewAllResultsHref);
    }
  };

  const handleAuxClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 1) return;

    setSearchTransitionSource(SearchTransitionSource.KeywordsAds);
  };

  if (ads.length === 0) return null;

  return (
    <S.Section data-testid={SearchResultsPageTestIds.keywordsAdsSection}>
      <S.TitleBox>
        <S.Title as="h2">{title}</S.Title>
      </S.TitleBox>
      <AdCards
        ads={ads}
        chunkSize={AdsChunkSize.Middle}
        adTransitionSource={AdTransitionSource.SearchResultsSimilarAds}
      />
      {viewAllResultsHref !== null && (
        <S.ButtonWrapper>
          <CustomLink
            href={cleanupSeoUrl(viewAllResultsHref)}
            dataTestId={SearchResultsPageTestIds.keywordsAdsViewAllButton}
            onClick={handleClick}
            onAuxClick={handleAuxClick}
          >
            <Button
              size="m"
              height={54}
              borderRadius={14}
              isFullWidth={isMobile}
              appearance="secondaryLight"
            >
              {t('pages.ads.view_all_results')}
            </Button>
          </CustomLink>
        </S.ButtonWrapper>
      )}
    </S.Section>
  );
};
